import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

import BaseTripItemSummaryCard from './BaseTripItemSummaryCard'
import { startPlaceOfTripItem } from '../../utils'

import { nonNullable } from 'lib/array/arrayUtils'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import { getMaxOccupancies } from 'lib/offer/occupancyUtils'
import { isVillaOffer } from 'lib/offer/offerTypes'
import BookedOfferDetailsCTA from 'tripPlanner/components/ItemCard/Common/BookedOfferDetailsCTA'
import PlannedHotelCtaContent from 'tripPlanner/components/ItemCard/Hotel/PlannedHotelCtaContent'
import { PlannedItemPriceContent } from 'tripPlanner/components/ItemCard/PlannedItemPriceContent'
import useOffer from 'hooks/Offers/useOffer'
import { usePlaceRating } from 'tripPlanner/hooks/Google/usePlaceRating'
import useAccommodationItemOccupancyDescription from 'tripPlanner/hooks/occupancy/useAccommodationItemOccupancyDescription'
import useAccommodationOfferAvailable from 'tripPlanner/hooks/useAccommodationOfferAvailable'
import type { AccommodationItem } from 'tripPlanner/types/tripItem'
import {
  formatShortDateRange,
  isCustomItem,
  isLeBooking,
  isLeBookmark,
} from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'
import { getAccommodationPriceAndLabel } from 'tripPlanner/utils/hotel'

interface Props {
  className?: string
  item: AccommodationItem
  showCloseButton?: boolean
  onClose?: () => void
}

const AccommodationSummaryCard = React.forwardRef<HTMLDivElement, Props>(
  ({ className, item, showCloseButton, onClose }, ref) => {
    const intl = useIntl()
    const place = startPlaceOfTripItem(item)
    const { data: rating } = usePlaceRating(
      place?.id ?? '',
      place?.id !== undefined && isCustomItem(item),
    )
    const offerId = item.code
    const [offer] = useOffer<App.BedbankOffer | App.Tours.TourV2Offer | App.Offer | App.CruiseOffer>(offerId)

    const offerImageId = useMemo(() => {
      if (!offer) return undefined
      if ('image' in offer) {
        return offer.image?.id
      } else if ('images' in offer) {
        return offer.images?.[0]?.id
      } else {
        return undefined
      }
    }, [offer])

    const offerLink = useMemo(() => {
      return isLeBookmark(item) ?
          buildProductLink(item, { offer }) :
        undefined
    }, [item, offer])

    const isOfferAvailable = useAccommodationOfferAvailable(
      offer,
      item.savedItemData,
      item.code,
    )

    // for rental property descriptors
    const propertyDescription = useMemo(() => {
      if (isVillaOffer(offer)) {
        const villaOffer = offer
        const allCapacities = villaOffer?.packages.flatMap(
          (p) => p.roomRate.capacities,
        )
        const max = getMaxOccupancies(
          allCapacities,
          { adults: 0 },
          {
            maxChildAge: villaOffer?.property.maxChildAge,
            maxInfantAge: villaOffer?.property.maxInfantAge,
            maxTeenagerAge: villaOffer?.property.maxTeenagerAge,
          },
        )
        const maxGuests = intl.formatMessage({
          id: '04NXgzCA8GcdWpFeUbOX',
          defaultMessage: 'Up to {count, plural, one {# guest} other {# guests}}',
          description: 'Maximum guest capacity for villa property on the rental page',
        }, { count: max.adults })
        const numberOfBedrooms = intl.formatMessage({
          id: 'cgP3UeFNM8LbN-bdgtAm',
          defaultMessage: '{count, plural, one {# bedroom} other {# bedrooms}}',
          description: 'Number of bedrooms in villa property on the hotel or room booking page',
        }, { count: villaOffer?.property.bedrooms ?? 0 })
        const numberOfBathrooms = intl.formatMessage({
          id: '6DMptmbZiWs6lhx7BS90',
          defaultMessage: '{count, plural, one {# bathroom} other {# bathrooms}}',
          description: 'Number of bathrooms in villa property on the hotel or room booking page',
        }, { count: villaOffer?.property.bathrooms ?? 0 })

        const sep = intl.formatMessage(middleDotSeparatorMessage)
        return nonNullable([
          'Entire Villa',
          maxGuests,
          numberOfBedrooms,
          numberOfBathrooms,
        ]).join(` ${sep} `)
      }
    }, [offer, intl])

    const priceAreaContent = useMemo(() => {
      if (!isLeBookmark(item)) return null
      if (offer?.parentType !== 'hotel') return null
      const { discountPercent, currencyCode } = item.savedItemData
      const { price, luxPlusPrice, priceLabel } =
        getAccommodationPriceAndLabel(offer, item.savedItemData)

      return (
        <PlannedItemPriceContent
          discountPercent={discountPercent}
          price={price}
          luxPlusPrice={luxPlusPrice}
          priceLabel={priceLabel}
          currencyCode={currencyCode}
          isOfferAvailable={isOfferAvailable}
        />
      )
    }, [isOfferAvailable, item, offer])

    const ctaAreaContent = useMemo(() => {
      if (offer?.parentType !== 'hotel') return null

      if (isLeBookmark(item) && offerLink) {
        return (
          <>
            <div />
            <PlannedHotelCtaContent
              item={item}
              isOfferAvailable={isOfferAvailable}
              variant="mapSummary"
              offerLink={offerLink}
            />
          </>
        )
      } else if (isLeBooking(item)) {
        return (
          <>
            <div />
            <BookedOfferDetailsCTA item={item} size="small" />
          </>
        )
      } else {
        return null
      }
    }, [isOfferAvailable, item, offer?.parentType, offerLink])

    const roomDescription = item.savedItemData?.roomTypeName
    const timeDescription = formatShortDateRange(item)
    const { occupanciesText, numberOfRoomsText } =
      useAccommodationItemOccupancyDescription(item)
    const timeAndOccupancyText = nonNullable([timeDescription, occupanciesText])
      .filter((text) => text.length > 0)
      .join(', ')

    if (!place) {
      return null
    }

    return (
      <BaseTripItemSummaryCard
        ref={ref}
        className={className}
        item={item}
        showCloseButton={showCloseButton}
        onClose={onClose}
        offerImageId={offerImageId}
        offerLink={offerLink}
        rating={rating}
        place={place}
        priceAreaContent={priceAreaContent}
        ctaAreaContent={ctaAreaContent}
        descriptors={[timeAndOccupancyText, numberOfRoomsText, roomDescription]}
        propertyDescription={propertyDescription}
      />
    )
  },
)

AccommodationSummaryCard.displayName = 'HotelItemSummaryCard'
export default AccommodationSummaryCard
